// src/app/jobs/browse-jobs/browse-jobs.service.ts
import { Injectable } from '@angular/core';
import {
  JOB_FILTER_DIMENSIONS,
  JobFilterLookup,
} from '@/app/models/lookups/job-filters.lookup';

export interface TaxonomyValue {
  id: string;
  label: string;
}

export interface TaxonomyDimension {
  param: string;
  values: Record<string, TaxonomyValue>;
}

export interface ActiveFilter {
  dimension: string;
  dimensionLabel: string;
  value: string;
  valueLabel: string;
  id: string;
}

export interface BrowsePageContext {
  filters: ActiveFilter[];
  title: string;
  description: string;
  summary: string;
}

export interface TaxonomyItem {
  slug: string;
  label: string;
  count: number;
  url: string;
}

export interface CountLookup {
  [dimension: string]: { [id: string]: number };
}

function buildTaxonomyMaps(
  dimensions: typeof JOB_FILTER_DIMENSIONS,
): Record<string, TaxonomyDimension> {
  const maps: Record<string, TaxonomyDimension> = {};
  for (const [dimension, config] of Object.entries(dimensions)) {
    const values: Record<string, TaxonomyValue> = {};
    for (const lookup of config.values as readonly JobFilterLookup[]) {
      values[lookup.key] = { id: lookup.id, label: lookup.name };
    }
    maps[dimension] = { param: config.param, values };
  }
  return maps;
}

@Injectable({ providedIn: 'root' })
export class BrowseJobsService {

  readonly dimensionLabels: Record<string, string> = {
    'state':        'State',
    'driver-type':  'Driver Type',
    'cdl-class':    'CDL Class',
    'freight-type': 'Freight Type',
    'endorsement':  'Endorsement',
    'experience':   'Experience Level',
  };

  readonly sectionLabels: Record<string, string> = {
    'state':        'Browse by State',
    'driver-type':  'Browse by Driver Type',
    'cdl-class':    'Browse by CDL Class',
    'freight-type': 'Browse by Freight Type',
    'endorsement':  'Browse by Endorsement',
    'experience':   'Browse by Experience Level',
  };

  readonly dimensionCountKeys: Record<string, string> = {
    'state':        'byState',
    'driver-type':  'byDriverType',
    'cdl-class':    'byCdlClass',
    'freight-type': 'byFreightType',
    'endorsement':  'byEndorsement',
    'experience':   'byExperienceType',
  };

  readonly taxonomyMaps: Record<string, TaxonomyDimension> =
    buildTaxonomyMaps(JOB_FILTER_DIMENSIONS);

  // ── Segment parsing ────────────────────────────────────────────────────

  parseSegments(segments: string[]): ActiveFilter[] | null {
    if (segments.length % 2 !== 0) return null;

    const filters: ActiveFilter[] = [];
    const pairs = this.chunkPairs(segments);

    for (const [dimension, value] of pairs) {
      const dim = this.taxonomyMaps[dimension];
      if (!dim) return null;

      const val = dim.values[value];
      if (!val) return null;

      filters.push({
        dimension,
        dimensionLabel: this.dimensionLabels[dimension] ?? dimension,
        value,
        valueLabel: val.label,
        id: val.id,
      });
    }

    return filters;
  }

  resolveApiParams(filters: ActiveFilter[]): Record<string, string> {
    const params: Record<string, string> = {};
    for (const filter of filters) {
      const dim = this.taxonomyMaps[filter.dimension];
      if (dim) params[dim.param] = filter.id;
    }
    return params;
  }

  buildPageContext(filters: ActiveFilter[]): BrowsePageContext {
    const values = filters.map(f => f.valueLabel);

    let title = 'Browse Truck Driving Jobs';
    if (values.length === 1)      title = `${values[0]} Truck Driving Jobs`;
    else if (values.length === 2) title = `${values[0]} · ${values[1]} Truck Driving Jobs`;
    else if (values.length > 2)   title = `${values.join(' · ')} Truck Driving Jobs`;

    let description = 'Browse truck driving jobs by state, driver type, CDL class, freight type, endorsement, and experience level.';
    if (values.length > 0) {
      description = `Browse ${values.join(' and ')} truck driving jobs. Refine your search by state, driver type, CDL class, freight type, endorsement, and experience level.`;
    }

    let summary = 'Browse truck driving jobs';
    if (values.length === 1)    summary = `Showing ${values[0]} jobs`;
    else if (values.length > 1) summary = `Showing jobs for ${values.join(' and ')}`;

    return { filters, title, description, summary };
  }

  buildCountLookup(counts: Record<string, { id: string; count: number }[]>): CountLookup {
    const lookup: CountLookup = {};
    for (const [dimension, entries] of Object.entries(counts)) {
      lookup[dimension] = {};
      for (const entry of entries) {
        lookup[dimension][entry.id] = entry.count;
      }
    }
    return lookup;
  }

  getItemsForDimension(
    dimensionSlug: string,
    countLookup: CountLookup,
    pathSegments: string[],
    sortByCount = true,
  ): TaxonomyItem[] {
    const dim      = this.taxonomyMaps[dimensionSlug];
    const countKey = this.dimensionCountKeys[dimensionSlug];
    if (!dim || !countKey) return [];

    const countsFor = countLookup[countKey] ?? {};
    const items: TaxonomyItem[] = [];

    for (const [valueSlug, value] of Object.entries(dim.values)) {
      const count = countsFor[value.id] ?? 0;
      if (count === 0) continue;

      const newSegments = [...pathSegments, dimensionSlug, valueSlug];
      items.push({
        slug:  valueSlug,
        label: value.label,
        count,
        url:   `/browse-jobs/${newSegments.join('/')}`,
      });
    }

    if (sortByCount) {
      items.sort((a, b) => b.count - a.count);
    }

    return items;
  }

  private chunkPairs(arr: string[]): [string, string][] {
    const pairs: [string, string][] = [];
    for (let i = 0; i < arr.length; i += 2) {
      pairs.push([arr[i], arr[i + 1]]);
    }
    return pairs;
  }
}
